import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';

interface AuthContextType {
  token: string | null;
  isAuthenticated: boolean;
  login: (credentials: { email: string; password: string }) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const getErrorMessage = (error: unknown, fallback: string) => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const responseError = error as { response?: { data?: { message?: string } }; message?: string };
    return responseError.response?.data?.message || responseError.message || fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true); // Check initial auth status

  // Check for existing token in localStorage on initial load
  useEffect(() => {
    const verifyStoredToken = async () => {
      const storedToken = localStorage.getItem('authToken');

      if (!storedToken) {
        setIsLoading(false);
        return;
      }

      try {
        await api.get('/auth/verify');
        setToken(storedToken);
      } catch {
        setToken(null);
        localStorage.removeItem('authToken');
      } finally {
        setIsLoading(false);
      }
    };

    const handleExpiredSession = () => {
      setToken(null);
      localStorage.removeItem('authToken');
    };

    window.addEventListener('auth:expired', handleExpiredSession);
    verifyStoredToken();

    return () => window.removeEventListener('auth:expired', handleExpiredSession);
  }, []);

  const login = async (credentials: { email: string; password: string }): Promise<boolean> => {
    setIsLoading(true);
    try {
      const response = await api.post('/auth/login', credentials);
      const data = response.data;
      if (data.token) {
        setToken(data.token);
        localStorage.setItem('authToken', data.token); // Store token
        toast.success('Login successful!');
        setIsLoading(false);
        return true;
      } else {
        throw new Error('No token received');
      }
    } catch (error: unknown) {
      console.error('Login error:', error);
      const message = getErrorMessage(error, 'Unknown error');
      toast.error(`Login failed: ${message}`);
      setToken(null);
      localStorage.removeItem('authToken');
      setIsLoading(false);
      return false;
    }
  };

  const logout = () => {
    setToken(null);
    localStorage.removeItem('authToken'); // Remove token
    toast.success('Logged out successfully.');
  };

  const isAuthenticated = !!token;

  return (
    <AuthContext.Provider value={{ token, isAuthenticated, login, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
